import { BudgetMemberEntry } from '@/services/types/budgets.types';
import { Invoice } from '@/services/types/invoice.types';
import { WorkActivity } from '@/services/types/work-activity.types';
import { can } from '@/permissions/budgetPermissions';
import { BudgetRole } from '@/permissions/roles';

export const getCurrentBudgetMember = (
  members: BudgetMemberEntry[] = [],
  currentUser?: { id?: string; email?: string } | null
): BudgetMemberEntry | null => {
  if (!currentUser) {
    return null;
  }

  return (
    members.find(
      (member) =>
        (currentUser.id && member.user_id === currentUser.id) ||
        (currentUser.email && member.email?.toLowerCase() === currentUser.email.toLowerCase())
    ) ?? null
  );
};

export const isInvoiceCreator = (
  invoice: Invoice,
  currentMember: BudgetMemberEntry | null,
  currentUser?: { id?: string } | null
): boolean => {
  if (invoice.creator?.member_id && currentMember?._id) {
    return invoice.creator.member_id === currentMember._id;
  }

  if (invoice.creator?.user_id && currentMember?.user_id) {
    return invoice.creator.user_id === currentMember.user_id;
  }

  if (invoice.creator?.user_id && currentUser?.id) {
    return invoice.creator.user_id === currentUser.id;
  }

  if (invoice.user_id && currentMember?.user_id) {
    return invoice.user_id === currentMember.user_id;
  }

  if (invoice.user_id && currentUser?.id) {
    return invoice.user_id === currentUser.id;
  }

  return false;
};

const getInvoiceCreatorMember = (invoice: Invoice, members: BudgetMemberEntry[] = []): BudgetMemberEntry | null => {
  if (invoice.creator?.member_id) {
    const byMemberId = members.find((member) => member._id === invoice.creator!.member_id);
    if (byMemberId) {
      return byMemberId;
    }
  }

  if (invoice.creator?.user_id) {
    const byUserId = members.find((member) => member.user_id === invoice.creator!.user_id);
    if (byUserId) {
      return byUserId;
    }
  }

  if (invoice.user_id) {
    return members.find((member) => member.user_id === invoice.user_id) ?? null;
  }

  return null;
};

export const isInvoiceCreatedByOwner = (invoice: Invoice, members: BudgetMemberEntry[] = []): boolean => {
  if (invoice.creator?.role === 'OWNER') {
    return true;
  }

  const creatorMember = getInvoiceCreatorMember(invoice, members);
  return creatorMember?.role === 'OWNER';
};

export const canModifyInvoice = (
  invoice: Invoice | null,
  currentMember: BudgetMemberEntry | null,
  currentUser?: { id?: string } | null
): boolean => {
  if (!invoice) {
    return false;
  }

  if (invoice.budget_id) {
    if (!currentMember || !can(currentMember.role, 'invoice:edit')) {
      return false;
    }

    return isInvoiceCreator(invoice, currentMember, currentUser);
  }

  return !!currentUser?.id && invoice.user_id === currentUser.id;
};

export const canDeleteInvoice = (
  invoice: Invoice | null,
  currentMember: BudgetMemberEntry | null,
  currentUser?: { id?: string } | null
): boolean => {
  if (!invoice) {
    return false;
  }

  if (invoice.budget_id) {
    if (!currentMember || !can(currentMember.role, 'invoice:delete')) {
      return false;
    }

    return isInvoiceCreator(invoice, currentMember, currentUser);
  }

  return !!currentUser?.id && invoice.user_id === currentUser.id;
};

export const canValidateInvoice = (
  invoice: Invoice | null,
  currentMember: BudgetMemberEntry | null,
  members: BudgetMemberEntry[] = []
): boolean => {
  if (!invoice || !currentMember || invoice.is_validated || invoice.is_draft) {
    return false;
  }

  if (!can(currentMember.role, 'invoice:validate')) {
    return false;
  }

  const isSelfCreated =
    (!!invoice.creator?.member_id && invoice.creator.member_id === currentMember._id) ||
    (!!invoice.creator?.user_id && invoice.creator.user_id === currentMember.user_id) ||
    (!invoice.creator && !!invoice.user_id && invoice.user_id === currentMember.user_id);

  // Invoice creators may validate only when they are also the budget owner
  if (isSelfCreated && currentMember.role !== 'OWNER') {
    return false;
  }

  if (currentMember.role === 'VALIDATOR' && isInvoiceCreatedByOwner(invoice, members)) {
    return false;
  }

  return true;
};

export const canValidateWorkActivity = (
  workActivity: WorkActivity | null,
  currentMember: BudgetMemberEntry | null
): boolean => {
  if (!workActivity || !currentMember || workActivity.status !== 'PENDING') {
    return false;
  }

  if (!can(currentMember.role, 'workActivity:validate')) {
    return false;
  }

  if (workActivity.creator.member_id && workActivity.creator.member_id === currentMember._id) {
    return false;
  }

  if (workActivity.creator.user_id && workActivity.creator.user_id === currentMember.user_id) {
    return false;
  }

  return true;
};

export const createBudgetPolicies = (
  role: BudgetRole | null,
  member: BudgetMemberEntry | null,
  currentUser?: { id?: string; email?: string } | null,
  members: BudgetMemberEntry[] = []
) => ({
  canModifyInvoice: (invoice: Invoice | null) => canModifyInvoice(invoice, member, currentUser),
  canDeleteInvoice: (invoice: Invoice | null) => canDeleteInvoice(invoice, member, currentUser),
  canValidateInvoice: (invoice: Invoice | null) => canValidateInvoice(invoice, member, members),
  canValidateWorkActivity: (workActivity: WorkActivity | null) => canValidateWorkActivity(workActivity, member)
});
